﻿import { useEffect, useMemo, useState } from "react";
import { useSession } from "@/auth/session";
import { getSettingsSnapshot, updateSecuritySettings } from "@/lib/api";

type ChannelState = {
  email: boolean;
  sms: boolean;
  sistema: boolean;
};

type AlertDefinition = {
  id: string;
  label: string;
  desc: string;
};

const ALERT_DEFINITIONS: AlertDefinition[] = [
  { id: "morosidad", label: "Clientes en morosidad", desc: "Aviso cuando un cliente supera los días de gracia sin pagar" },
  { id: "corte", label: "Cortes programados", desc: "Notificación antes de ejecutar un corte de suministro" },
  { id: "stock", label: "Stock bajo en bodega", desc: "Alerta cuando un producto cae bajo el mínimo configurado" },
  { id: "lectura", label: "Período de lecturas abierto", desc: "Recordatorio al inicio del período de toma de lecturas" },
  { id: "cierre", label: "Cierre de período", desc: "Confirmación al completar el cierre mensual de facturación" },
  { id: "consumo_alto", label: "Consumo anormal detectado", desc: "Alerta cuando el consumo de un cliente supera el umbral definido" },
];

const DEFAULT_CHANNELS: Record<string, ChannelState> = {
  morosidad: { email: true, sms: false, sistema: true },
  corte: { email: true, sms: true, sistema: true },
  stock: { email: true, sms: false, sistema: true },
  lectura: { email: true, sms: false, sistema: true },
  cierre: { email: true, sms: false, sistema: true },
  consumo_alto: { email: false, sms: false, sistema: true },
};

function normalizeChannels(
  input: Record<string, { email?: boolean; sms?: boolean; sistema?: boolean }> | null | undefined,
): Record<string, ChannelState> {
  const result: Record<string, ChannelState> = {};
  for (const definition of ALERT_DEFINITIONS) {
    const source = input?.[definition.id];
    const fallback = DEFAULT_CHANNELS[definition.id];
    result[definition.id] = {
      email: typeof source?.email === "boolean" ? source.email : fallback.email,
      sms: typeof source?.sms === "boolean" ? source.sms : fallback.sms,
      sistema: typeof source?.sistema === "boolean" ? source.sistema : fallback.sistema,
    };
  }
  return result;
}

export default function NotificacionesTab() {
  const { session } = useSession();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const [channels, setChannels] = useState<Record<string, ChannelState>>(() => normalizeChannels(null));
  const [diasGracia, setDiasGracia] = useState("5");
  const [umbralConsumo, setUmbralConsumo] = useState("80");

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError("");
      try {
        const snapshot = await getSettingsSnapshot(session);
        if (cancelled) return;
        const security = snapshot.security;
        setChannels(normalizeChannels(security?.alertChannelsJson));
        setDiasGracia(String(security?.delinquencyGraceDays ?? 5));
        setUmbralConsumo(String(security?.abnormalConsumptionThresholdPercent ?? 80));
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "No se pudo cargar configuración de notificaciones");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const rows = useMemo(
    () => ALERT_DEFINITIONS.map((definition) => ({ definition, state: channels[definition.id] ?? DEFAULT_CHANNELS[definition.id] })),
    [channels],
  );

  function toggle(id: string, channel: keyof ChannelState): void {
    setChannels((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] ?? DEFAULT_CHANNELS[id]),
        [channel]: !(prev[id]?.[channel] ?? DEFAULT_CHANNELS[id][channel]),
      },
    }));
    setSaved(false);
  }

  async function handleSave(): Promise<void> {
    if (!session) return;
    const grace = Number(diasGracia);
    const threshold = Number(umbralConsumo);
    if (!Number.isFinite(grace) || grace < 0) {
      setError("Días de gracia inválido");
      return;
    }
    if (!Number.isFinite(threshold) || threshold < 0) {
      setError("Umbral de consumo inválido");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const normalizedChannels = Object.fromEntries(
        Object.entries(channels).map(([key, value]) => [
          key,
          {
            email: value.email,
            sms: false,
            sistema: value.sistema,
          },
        ]),
      ) as Record<string, { email?: boolean; sms?: boolean; sistema?: boolean }>;

      await updateSecuritySettings(session, {
        alertChannelsJson: normalizedChannels,
        delinquencyGraceDays: Math.trunc(grace),
        abnormalConsumptionThresholdPercent: Math.trunc(threshold),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar configuración");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="text-sm text-gray-500">Cargando configuración...</div> : null}

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50">
            <i className="ri-notification-3-line text-blue-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Configuración de Alertas</h2>
            <p className="text-xs text-gray-400">Define qué eventos generan notificaciones y por qué canal</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Evento</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <div className="flex items-center justify-center gap-1">
                    <i className="ri-mail-line" /> Email
                  </div>
                </th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <div className="flex items-center justify-center gap-1">
                    <i className="ri-computer-line" /> Sistema
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ definition, state }) => (
                <tr key={definition.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800 text-sm">{definition.label}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{definition.desc}</div>
                  </td>
                  {(["email", "sistema"] as const).map((channel) => (
                    <td key={channel} className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggle(definition.id, channel)}
                        className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer mx-auto block ${
                          state[channel] ? "bg-emerald-500" : "bg-gray-200"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${
                            state[channel] ? "left-5" : "left-0.5"
                          }`}
                        />
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50">
            <i className="ri-alarm-warning-line text-amber-600" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-gray-800">Umbrales de Alerta</h2>
            <p className="text-xs text-gray-400">Parámetros que disparan las notificaciones automáticas</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Días de gracia antes de alerta de morosidad</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={diasGracia}
                onChange={(e) => {
                  setDiasGracia(e.target.value);
                  setSaved(false);
                }}
                className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
              />
              <span className="text-sm text-gray-500">días</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Umbral de consumo anormal (% sobre promedio)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={umbralConsumo}
                onChange={(e) => {
                  setUmbralConsumo(e.target.value);
                  setSaved(false);
                }}
                className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 transition-colors"
              />
              <span className="text-sm text-gray-500">% sobre promedio</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {saved ? (
          <div className="flex items-center gap-2 text-emerald-600 text-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-check-line" />
            </div>
            Configuración guardada
          </div>
        ) : null}
        <button
          onClick={() => void handleSave()}
          disabled={saving || loading}
          className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          {saving ? "Guardando..." : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}
